import { useCallback, useEffect, useState } from 'react'

const ACTIVE_OFFSET = 24

const SCROLL_LOCK_MS = 400

const SECTION_ATTRIBUTE = 'data-section'

const getSectionNodes = (container: HTMLElement) =>
  [...container.querySelectorAll<HTMLElement>(`[${SECTION_ATTRIBUTE}]`)].map(
    (node) => ({ key: node.getAttribute(SECTION_ATTRIBUTE), node })
  )

export const useScrollSpy = (keys: string[]) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  const [activeKey, setActiveKey] = useState<string | undefined>(keys[0])
  const [lockedUntil, setLockedUntil] = useState(0)

  const scrollTo = useCallback(
    (key: string) => {
      if (!container) return

      const target = getSectionNodes(container).find(
        (section) => section.key === key
      )

      if (!target) return

      setLockedUntil(performance.now() + SCROLL_LOCK_MS)
      setActiveKey(key)

      const top =
        target.node.getBoundingClientRect().top -
        container.getBoundingClientRect().top +
        container.scrollTop

      container.scrollTo({ top, behavior: 'smooth' })
    },
    [container]
  )

  useEffect(() => {
    if (!container) return

    let frame = 0

    const update = () => {
      frame = 0

      if (performance.now() < lockedUntil) return

      const sections = getSectionNodes(container)
      const containerTop = container.getBoundingClientRect().top

      let current = sections[0]?.key

      sections.forEach(({ key, node }) => {
        if (node.getBoundingClientRect().top - containerTop <= ACTIVE_OFFSET) {
          current = key
        }
      })

      const isAtBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight <=
        2

      if (isAtBottom) {
        current = sections.at(-1)?.key ?? current
      }

      setActiveKey(current ?? undefined)
    }

    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(update)
    }

    container.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      container.removeEventListener('scroll', onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [container, lockedUntil])

  useEffect(() => {
    setActiveKey((current) =>
      current && keys.includes(current) ? current : keys[0]
    )
  }, [keys])

  return { setContainer, activeKey, scrollTo }
}
